import React from "react";
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { classNames } from "src/helpers/classNames";

export const CustomAppAnalysis = ({ data }) => {
  const analysis = data?.analysis || {};
  const issues = analysis.issues || [];
  const fixes = analysis.fixes || [];

  return (
    <div className="animate-fade-in space-y-6 pt-2">
      {/* 1. Health Status Banner */}
      <div
        className={classNames(
          "overflow-hidden rounded-2xl border p-6 shadow-sm transition-all",
          analysis.ok
            ? "border-emerald-200/80 bg-gradient-to-r from-emerald-50/80 via-teal-50/30 to-white"
            : "border-amber-200/80 bg-gradient-to-r from-amber-50/80 via-orange-50/30 to-white"
        )}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div
              className={classNames(
                "flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-white shadow-sm ring-1",
                analysis.ok
                  ? "text-emerald-600 ring-emerald-100"
                  : "text-amber-600 ring-amber-100"
              )}
            >
              {analysis.ok ? (
                <ShieldCheckIcon className="h-6 w-6" />
              ) : (
                <SparklesIcon className="h-6 w-6" />
              )}
            </div>

            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-gray-900">
                  Build Analysis & Container Compatibility
                </h3>
                <span
                  className={classNames(
                    "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold",
                    analysis.ok
                      ? "bg-emerald-100 text-emerald-800"
                      : "bg-amber-100 text-amber-800"
                  )}
                >
                  <span
                    className={classNames(
                      "h-1.5 w-1.5 rounded-full",
                      analysis.ok ? "bg-emerald-500" : "bg-amber-500"
                    )}
                  />
                  {analysis.ok ? "Compatible" : "Action Needed"}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                Automated pre-flight inspection of codebase, dependencies, and port bindings
              </p>
            </div>
          </div>

          {analysis.canAutoFix && (
            <span className="inline-flex items-center gap-1.5 rounded-xl border border-purple-200 bg-purple-50 px-3 py-1.5 text-xs font-semibold text-purple-700 shadow-2xs">
              <WrenchScrewdriverIcon className="h-4 w-4" />
              <span>Auto-Fix Enabled</span>
            </span>
          )}
        </div>

        {analysis.summary && (
          <div className="mt-4 rounded-xl border border-white/80 bg-white/90 p-4 text-xs leading-relaxed text-slate-700 shadow-2xs backdrop-blur-sm">
            <span className="font-semibold text-slate-900">Architect Summary: </span>
            {analysis.summary}
          </div>
        )}
      </div>

      {/* 2-Column Grid: Issues & Fixes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Issues List */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
                <ExclamationTriangleIcon className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-900">
                  Detected Issues
                </h4>
                <p className="text-xs text-gray-400">
                  {issues.length} potential friction point{issues.length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
          </div>

          {issues.length > 0 ? (
            <div className="space-y-2.5">
              {issues.map((issue, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-3 rounded-xl border border-gray-100 bg-slate-50/70 p-3.5 hover:bg-slate-50 transition-colors"
                >
                  <span
                    className={classNames(
                      "mt-0.5 flex-shrink-0 rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                      issue.level === "fixable"
                        ? "bg-sky-100 text-sky-700 border border-sky-200"
                        : "bg-rose-100 text-rose-700 border border-rose-200"
                    )}
                  >
                    {issue.level || "Notice"}
                  </span>
                  <span className="text-xs text-slate-700 leading-relaxed">
                    {issue.text}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-xs text-gray-400">
              No configuration or engine issues detected in this build.
            </div>
          )}
        </div>

        {/* Applied Transformations */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-xs">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                <CheckCircleIcon className="h-4 w-4" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-gray-900">
                  Applied Transformations
                </h4>
                <p className="text-xs text-gray-400">
                  {fixes.length} automatic deployment patch{fixes.length !== 1 ? "es" : ""}
                </p>
              </div>
            </div>
          </div>

          {fixes.length > 0 ? (
            <div className="space-y-2.5">
              {fixes.map((fix, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/30 p-3 hover:bg-emerald-50/60 transition-colors"
                >
                  <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-emerald-600" />
                  <span className="text-xs font-medium text-slate-800">
                    {fix}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-xs text-gray-400">
              Application ran clean out-of-the-box without requiring code rewrites.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
